import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { propertiesService } from "../services/firebaseServices";

const LOCATION_LINES = [
  "TELLURIDE, CO | BEAVER CREEK, CO | DENVER, CO",
  "DEL MAR, CA | MIAMI BEACH, FL",
];

const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isDesktopDropdownOpen, setIsDesktopDropdownOpen] = useState(false);

  // State to hold dynamic menu items (now grouped)
  const [groupedHomes, setGroupedHomes] = useState([]);
  const location = useLocation();

  // Fetch properties and group them
  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const data = await propertiesService.getAll();

        // Define the hierarchy order
        const groups = [
          {
            name: "Beaver Creek",
            matcher: (loc) =>
              loc && (loc.includes("Beaver Creek") || loc.includes("Avon")),
            items: [],
          },
          {
            name: "Telluride",
            matcher: (loc) =>
              loc &&
              (loc.includes("Telluride") || loc.includes("Mountain Village")),
            items: [],
          },
          {
            name: "Denver",
            matcher: (loc) => loc && loc.includes("Denver"),
            items: [],
          },
          {
            name: "San Diego",
            matcher: (loc) =>
              loc && (loc.includes("San Diego") || loc.includes("Del Mar")),
            items: [],
          },
          {
            name: "Miami Beach",
            matcher: (loc) =>
              loc && (loc.includes("Miami") || loc.includes("Miami Beach")),
            items: [],
          },
        ];

        // Sort properties into groups
        data.forEach((property) => {
          const loc = property.location;
          const item = {
            title: property.title,
            path: `/homes/${property.slug || property.id}`,
          };

          // Find matching group
          const group = groups.find((g) => g.matcher(loc));
          if (group) {
            group.items.push(item);
          }
        });

        setGroupedHomes(groups);
      } catch (error) {
        console.error("Failed to fetch menu items:", error);
      }
    };

    fetchProperties();
  }, []);

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setIsDropdownOpen(false);
  };

  const visibleGroupCount = groupedHomes.filter(
    (g) => g.items.length > 0,
  ).length;

  const isActive = (path) =>
    location.pathname === path
      ? "text-acf-gold"
      : "text-acf-blue hover:text-acf-gold";

  return (
    <nav className="bg-white shadow-sm fixed w-full z-50 top-0">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-24">
          {/* 1. Logo Area */}
          <div className="flex-shrink-0 flex items-center z-50 relative">
            <Link
              to="/"
              onClick={closeMobileMenu}
              className="flex items-center gap-4 no-underline"
            >
              <img
                src="/images/logo.png"
                alt="ACF Luxury Stays"
                className="h-16 w-auto object-contain flex-shrink-0"
                onError={(e) => (e.target.style.display = "none")}
              />

              <div className="flex flex-col text-left">
                <h1 className="font-primary text-acf-blue text-xl md:text-2xl font-bold tracking-widest leading-none m-0 whitespace-nowrap">
                  ACF LUXURY STAYS
                </h1>
                {/* Split across two fixed lines so the locations stay narrower
                    than the title and never run into the nav links. */}
                <span className="font-secondary text-acf-maroon text-[10px] lg:text-[11px] xl:text-xs tracking-wider mt-1 block leading-snug">
                  {LOCATION_LINES.map((line) => (
                    <span key={line} className="block whitespace-nowrap">
                      {line}
                    </span>
                  ))}
                </span>
              </div>
            </Link>
          </div>

          {/* 2. Desktop Menu */}
          <div className="hidden lg:flex lg:items-center lg:space-x-6 xl:space-x-8 flex-shrink-0">
            <Link
              to="/"
              className={`font-primary text-base uppercase tracking-wide no-underline whitespace-nowrap ${isActive("/")}`}
            >
              Home
            </Link>

            {/* Desktop Dropdown */}
            <div
              className="relative group"
              onMouseEnter={() => setIsDesktopDropdownOpen(true)}
              onMouseLeave={() => setIsDesktopDropdownOpen(false)}
            >
              <Link
                to="/homes"
                className={`font-primary text-base uppercase tracking-wide no-underline flex items-center whitespace-nowrap ${isActive("/homes")}`}
              >
                Our Luxury Homes
                <span className="ml-1 text-xs">▼</span>
              </Link>

              {/* Desktop Dropdown Content - Mega Menu */}
              {isDesktopDropdownOpen && (
                <div
                  className="absolute left-1/2 -translate-x-1/2 mt-0 max-w-[calc(100vw-2rem)] bg-white shadow-xl border-t-4 border-acf-gold rounded-b-sm p-8"
                  style={{
                    width: `${Math.max(1, visibleGroupCount) * 225}px`,
                  }}
                >
                  <div
                    className="grid gap-8"
                    style={{
                      gridTemplateColumns: `repeat(${Math.max(1, visibleGroupCount)}, minmax(0, 1fr))`,
                    }}
                  >
                    {groupedHomes.map(
                      (group) =>
                        group.items.length > 0 && (
                          <div key={group.name} className="flex flex-col">
                            {/* Group Header */}
                            <h3 className="text-xs font-bold uppercase tracking-widest text-acf-gold border-b border-gray-200 pb-3 mb-4 m-0">
                              {group.name}
                            </h3>

                            {/* Group Items */}
                            <div className="flex flex-col space-y-3">
                              {group.items.map((home, index) => (
                                <React.Fragment key={home.path}>
                                  {index > 0 && (
                                    <div className="border-t border-gray-100" />
                                  )}
                                  <Link
                                    to={home.path}
                                    className="text-sm font-primary text-gray-700 hover:text-acf-blue hover:translate-x-1 transition-all duration-200 no-underline leading-snug"
                                  >
                                    {home.title}
                                  </Link>
                                </React.Fragment>
                              ))}
                            </div>
                          </div>
                        ),
                    )}

                    {/* Fallback/Loading state */}
                    {visibleGroupCount === 0 && (
                      <span className="text-sm text-gray-400">Loading...</span>
                    )}
                  </div>
                </div>
              )}
            </div>

            <Link
              to="/about"
              className={`font-primary text-base uppercase tracking-wide no-underline whitespace-nowrap ${isActive("/about")}`}
            >
              About
            </Link>
            <Link
              to="/team"
              className={`font-primary text-base uppercase tracking-wide no-underline whitespace-nowrap ${isActive("/team")}`}
            >
              Meet the Team
            </Link>
            <Link
              to="/contact"
              className={`font-primary text-base uppercase tracking-wide no-underline whitespace-nowrap ${isActive("/contact")}`}
            >
              Contact
            </Link>

            <a
              href="#book-now"
              className="bg-acf-gold text-white font-primary px-6 py-2 rounded-sm uppercase text-base tracking-wider hover:bg-yellow-600 transition duration-300 no-underline whitespace-nowrap"
            >
              Book Now
            </a>
          </div>

          {/* 3. Mobile Hamburger Button */}
          <div className="lg:hidden z-50">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="text-acf-blue hover:text-acf-gold focus:outline-none p-2"
            >
              {isMobileMenuOpen ? (
                <svg
                  className="h-8 w-8"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              ) : (
                <svg
                  className="h-8 w-8"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* 4. Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="lg:hidden absolute top-24 left-0 w-full bg-white shadow-xl border-t border-gray-100 py-4 px-6 flex flex-col space-y-4 h-[calc(100vh-6rem)] overflow-y-auto">
          <Link
            to="/"
            onClick={closeMobileMenu}
            className="font-primary text-xl text-acf-blue uppercase tracking-wide border-b border-gray-100 pb-2"
          >
            Home
          </Link>

          {/* Mobile Accordion */}
          <div>
            <div
              className="flex justify-between items-center border-b border-gray-100 pb-2 cursor-pointer"
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            >
              <span className="font-primary text-xl text-acf-blue uppercase tracking-wide">
                Our Luxury Homes
              </span>
              <span
                className="text-acf-blue text-sm transform transition-transform duration-200"
                style={{
                  transform: isDropdownOpen ? "rotate(180deg)" : "rotate(0deg)",
                }}
              >
                ▼
              </span>
            </div>

            {/* Mobile Dropdown Content - Cleaned up to match desktop colors */}
            {isDropdownOpen && (
              <div className="flex flex-col mt-2 mb-2 bg-white pl-4 border-l-2 border-acf-gold/30">
                <Link
                  to="/homes"
                  onClick={closeMobileMenu}
                  className="font-primary text-sm text-acf-gold uppercase tracking-widest font-bold block py-3 border-b border-gray-100"
                >
                  View All Homes
                </Link>

                {groupedHomes.map(
                  (group, index) =>
                    group.items.length > 0 && (
                      <div
                        key={group.name}
                        className={
                          index !== groupedHomes.length - 1
                            ? "border-b border-gray-100 pb-2"
                            : "pb-2"
                        }
                      >
                        {/* Group Header */}
                        <div className="pt-4 pb-2">
                          <h3 className="text-xs font-bold text-acf-gold uppercase tracking-widest m-0">
                            {group.name}
                          </h3>
                        </div>

                        {/* Group Items */}
                        <div className="flex flex-col space-y-2 pt-1 pb-2">
                          {group.items.map((home, index) => (
                            <React.Fragment key={home.path}>
                              {index > 0 && (
                                <div className="border-t border-gray-100 mx-2" />
                              )}
                              <Link
                                to={home.path}
                                onClick={closeMobileMenu}
                                className="px-2 font-primary text-sm text-gray-600 hover:text-acf-blue no-underline leading-snug"
                              >
                                {home.title}
                              </Link>
                            </React.Fragment>
                          ))}
                        </div>
                      </div>
                    ),
                )}
              </div>
            )}
          </div>

          <Link
            to="/about"
            onClick={closeMobileMenu}
            className="font-primary text-xl text-acf-blue uppercase tracking-wide border-b border-gray-100 pb-2"
          >
            About
          </Link>

          <Link
            to="/team"
            onClick={closeMobileMenu}
            className="font-primary text-xl text-acf-blue uppercase tracking-wide border-b border-gray-100 pb-2"
          >
            Meet the Team
          </Link>

          <Link
            to="/contact"
            onClick={closeMobileMenu}
            className="font-primary text-xl text-acf-blue uppercase tracking-wide border-b border-gray-100 pb-2"
          >
            Contact
          </Link>

          <a
            href="#book-now"
            onClick={closeMobileMenu}
            className="bg-acf-gold text-white font-primary text-center py-4 rounded-sm uppercase text-lg tracking-widest mt-4"
          >
            Book Now
          </a>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
